/**
 * AtlasProTerminal — CoinMarketCap/ProRealTime-style trading terminal.
 *
 * Layout (1920 viewport):
 *   ┌──────────────────────────────────────────────────────┬───────────┐
 *   │ INSTRUMENT HEADER (ticker · price · 24h · mcap)     │           │
 *   ├──────────────────────────────────────────────────────┤  RIGHT    │
 *   │ TIMEFRAME PILLS  (15m / 1h / 4h / 1d / 1w)          │  SIDEBAR  │
 *   ├──────────────────────────────────────────────────────┤  (CMC     │
 *   │                                                      │   table)  │
 *   │                 CANDLESTICK CHART                    │           │
 *   │                 + volume histogram                   │           │
 *   │                                                      │           │
 *   └──────────────────────────────────────────────────────┴───────────┘
 *   <AtlasTerminal insights+strain strip embedded below>
 *
 * Keeps the existing AtlasTerminal component (strain + watchlist + ticker)
 * rendered below so we don't lose the SIGNAL context — this page is
 * ADDITIVE.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import AtlasCandlestickChart from './AtlasCandlestickChart';
import AtlasInstrumentTable from './AtlasInstrumentTable';
import AtlasTerminal from './AtlasTerminal';

const TIMEFRAMES = [
  { id: '15m', label: '15m' },
  { id: '1h',  label: '1H'  },
  { id: '4h',  label: '4H'  },
  { id: '1d',  label: '1D'  },
  { id: '1w',  label: '1W'  },
];
const DEFAULT_INTERVAL = '1h';
const DEFAULT_SYMBOL = 'BTC/USD';

function fmtPrice(p) {
  if (p == null) return '—';
  if (p < 1) return `$${p.toFixed(4)}`;
  return `$${p.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}
function fmtBig(m) {
  if (!m) return '—';
  if (m >= 1e12) return `$${(m / 1e12).toFixed(2)}T`;
  if (m >= 1e9)  return `$${(m / 1e9).toFixed(2)}B`;
  if (m >= 1e6)  return `$${(m / 1e6).toFixed(2)}M`;
  return `$${m.toFixed(0)}`;
}

export default function AtlasProTerminal({
  onSwitchAgent,
  onSwitchToNova,
  onSwitchToOrion,
  onSwitchToVega,
  onSwitchToShow,
  onSwitchToCrew,
}) {  // onSwitchAgent is reserved for future use; global SIGNAL chrome handles navigation.
  const [catalog, setCatalog] = useState({ items: [], count: 0 });
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState(DEFAULT_SYMBOL);
  const [interval, setInterval_] = useState(DEFAULT_INTERVAL);
  const [candles, setCandles] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartError, setChartError] = useState(null);
  // Silence unused-var while keeping the prop for future use.
  void onSwitchAgent;

  // ---- Fetch instrument catalog (60s cadence) ----
  const fetchCatalog = useCallback(async () => {
    try {
      const r = await api.get('/api/atlas/instruments/catalog');
      setCatalog(r.data);
    } catch (e) {
      // keep stale
      console.warn('[AtlasPro] catalog fetch failed', e);
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
    const id = window.setInterval(fetchCatalog, 60000);
    return () => window.clearInterval(id);
  }, [fetchCatalog]);

  // ---- Fetch OHLC on symbol/interval change (only if has_ohlc) ----
  const selectedMeta = useMemo(
    () => catalog.items.find((i) => i.symbol === selectedSymbol),
    [catalog.items, selectedSymbol]
  );
  const canChart = Boolean(selectedMeta?.has_ohlc);

  const fetchOhlc = useCallback(async () => {
    if (!canChart) {
      setCandles([]);
      setChartError(`Chart data not available for ${selectedSymbol}. Select a Kraken-listed asset (BTC, ETH, SOL, ADA, XRP, DOGE, or XAU).`);
      return;
    }
    setLoadingChart(true);
    setChartError(null);
    try {
      const r = await api.get(
        `/api/atlas/instruments/${encodeURIComponent(selectedSymbol)}/ohlc`,
        { params: { interval, limit: 200 } }
      );
      setCandles(r.data.candles || []);
    } catch (e) {
      setChartError(e?.response?.data?.detail || e?.message || 'chart fetch failed');
      setCandles([]);
    } finally {
      setLoadingChart(false);
    }
  }, [selectedSymbol, interval, canChart]);

  useEffect(() => {
    fetchOhlc();
    // Refresh the visible chart every 60s
    const id = window.setInterval(fetchOhlc, 60000);
    return () => window.clearInterval(id);
  }, [fetchOhlc]);

  const lastCandle = candles.length > 0 ? candles[candles.length - 1] : null;
  const displayPrice = lastCandle?.close ?? selectedMeta?.price;
  const changePct = selectedMeta?.change_pct_24h;

  return (
    <div
      data-testid="atlas-pro-terminal"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 80% 0%, rgba(30,40,60,0.35), #05070b 60%)',
        color: '#dbe3f2',
        fontFamily: 'ui-monospace, Menlo, monospace',
        // Extra top padding clears the persistent SIGNAL global chrome
        // (left ← BACK arrow + right user button). Left/right padding too.
        padding: '64px 100px 24px',
      }}
    >
      {/* Top title strip (no internal back button — global chrome handles nav) */}
      <div style={{
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 10, letterSpacing: '0.32em', color: '#f7c94b' }}>
          ATLAS · TRADING TERMINAL
        </div>
        <div style={{ fontSize: 18, marginTop: 4, letterSpacing: '0.06em' }}>
          {catalog.count} instruments · Kraken · CoinLore · Frankfurter
        </div>
      </div>

      {/* Split layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 420px',
        gap: 14,
        minHeight: 'calc(100vh - 420px)',
      }}>
        {/* LEFT — instrument header + chart */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          {/* Instrument stats header */}
          <div
            data-testid="atlas-pro-instrument-header"
            style={{
              padding: '12px 18px',
              background: 'rgba(5, 7, 11, 0.65)',
              border: '1px solid rgba(120, 150, 200, 0.18)',
              borderRadius: 6,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto auto auto',
              gap: 22,
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 9, letterSpacing: '0.28em', color: 'rgba(200,210,230,0.55)' }}>TICKER</div>
              <div style={{ fontSize: 22, color: '#f1f5f9', marginTop: 2 }}>{selectedSymbol}</div>
              <div style={{ fontSize: 10, color: 'rgba(200,210,230,0.45)', marginTop: 2 }}>
                {selectedMeta?.name || '—'}
              </div>
            </div>
            <div />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, letterSpacing: '0.24em', color: 'rgba(200,210,230,0.55)' }}>PRICE</div>
              <div style={{ fontSize: 22, color: '#e8e8e8', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                {fmtPrice(displayPrice)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, letterSpacing: '0.24em', color: 'rgba(200,210,230,0.55)' }}>24H</div>
              <div
                style={{
                  fontSize: 18,
                  marginTop: 2,
                  color: changePct == null ? 'rgba(200,210,230,0.45)' : changePct >= 0 ? '#4ade80' : '#f87171',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {changePct == null ? '—' : `${changePct >= 0 ? '▲ ' : '▼ '}${Math.abs(changePct).toFixed(2)}%`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, letterSpacing: '0.24em', color: 'rgba(200,210,230,0.55)' }}>MARKET CAP</div>
              <div style={{ fontSize: 16, color: '#e8e8e8', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                {fmtBig(selectedMeta?.market_cap)}
              </div>
            </div>
          </div>

          {/* Timeframe pills */}
          <div
            data-testid="atlas-pro-timeframes"
            style={{
              display: 'flex',
              gap: 6,
              padding: '8px 12px',
              background: 'rgba(5, 7, 11, 0.4)',
              border: '1px solid rgba(120, 150, 200, 0.15)',
              borderRadius: 4,
            }}
          >
            <span style={{ fontSize: 9, letterSpacing: '0.22em', color: 'rgba(200,210,230,0.55)', alignSelf: 'center', marginRight: 6 }}>
              TIMEFRAME
            </span>
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf.id}
                onClick={() => setInterval_(tf.id)}
                data-testid={`atlas-tf-${tf.id}`}
                style={{
                  padding: '4px 12px',
                  fontSize: 10,
                  letterSpacing: '0.22em',
                  background: interval === tf.id ? 'rgba(247, 201, 75, 0.15)' : 'transparent',
                  color: interval === tf.id ? '#f7c94b' : 'rgba(200,210,230,0.6)',
                  border: `1px solid ${interval === tf.id ? '#f7c94b' : 'rgba(120,150,200,0.2)'}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                {tf.label}
              </button>
            ))}
            {loadingChart && (
              <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(200,210,230,0.45)' }}>
                LOADING…
              </span>
            )}
          </div>

          {/* Chart panel */}
          <div
            data-testid="atlas-pro-chart-panel"
            style={{
              background: 'rgba(5, 7, 11, 0.65)',
              border: '1px solid rgba(120, 150, 200, 0.18)',
              borderRadius: 6,
              padding: 8,
              minHeight: 440,
              position: 'relative',
            }}
          >
            {chartError ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'rgba(200,210,230,0.55)', fontSize: 13 }}>
                {chartError}
              </div>
            ) : candles.length > 0 ? (
              <AtlasCandlestickChart candles={candles} height={440} />
            ) : (
              <div style={{ padding: 30, textAlign: 'center', color: 'rgba(200,210,230,0.35)', fontSize: 12 }}>
                {loadingChart ? 'Loading chart…' : 'Select an instrument to load chart.'}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — instrument table */}
        <div style={{ minHeight: 'calc(100vh - 440px)' }}>
          {loadingCatalog ? (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(200,210,230,0.4)',
              fontSize: 11,
              background: 'rgba(5, 7, 11, 0.5)',
              border: '1px solid rgba(120, 150, 200, 0.18)',
              borderRadius: 6,
            }}>
              Loading instruments…
            </div>
          ) : (
            <AtlasInstrumentTable
              items={catalog.items}
              selectedSymbol={selectedSymbol}
              onSelect={(row) => setSelectedSymbol(row.symbol)}
            />
          )}
        </div>
      </div>

      {/* Bottom — existing SIGNAL Atlas context (sectors/strain/ticker) */}
      <div style={{ marginTop: 16 }}>
        <div style={{
          fontSize: 9,
          letterSpacing: '0.3em',
          color: 'rgba(200,210,230,0.4)',
          marginBottom: 6,
        }}>
          SIGNAL · SECTOR CONTEXT
        </div>
        <AtlasTerminal
          onSwitchAgent={onSwitchAgent}
          onSwitchToNova={onSwitchToNova}
          onSwitchToOrion={onSwitchToOrion}
          onSwitchToVega={onSwitchToVega}
          onSwitchToShow={onSwitchToShow}
          onSwitchToCrew={onSwitchToCrew}
        />
      </div>
    </div>
  );
}
